import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d79mbh3ll6jbq2vddpp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="text-white" style={{ backgroundColor: '#0a1a0f' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Col 1 - Brand + Newsletter */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4 group">
              <img src="/logo.png" alt="GCSE Computing Logo" className="h-9 w-auto object-contain" />
              <span className="font-bold text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
                GCSE <span className="text-emerald-400">Computing</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">
              The UK&apos;s go-to Computer Science revision platform for GCSE and A Level students. Aligned to AQA, OCR, Edexcel and WJEC.
            </p>
            <p className="text-sm font-semibold text-white mb-3">Stay Updated</p>
            {submitted ? (
              <p className="text-emerald-400 text-sm font-medium">Thanks for subscribing!</p>
            ) : (
              <form data-readdy-form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-600 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '...' : 'Go'}
                </button>
              </form>
            )}
          </div>

          {/* Col 2 - Quick Links */}
          <div>
            <p className="text-sm font-semibold text-white mb-4">Quick Links</p>
            <ul className="flex flex-col gap-2.5">
              {[
                { label: 'GCSE Topics', path: '/gcse/topics' },
                { label: 'GCSE Past Papers', path: '/gcse/past-papers' },
                { label: 'GCSE Resources', path: '/gcse/resources' },
                { label: 'Predicted Papers', path: '/gcse/predicted-papers' },
                { label: 'A Level Topics', path: '/alevel/topics' },
                { label: 'Programming', path: '/programming' },
              ].map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className="text-slate-400 hover:text-emerald-400 text-sm transition-colors cursor-pointer">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 - Exam Boards */}
          <div>
            <p className="text-sm font-semibold text-white mb-4">Exam Boards</p>
            <ul className="flex flex-col gap-2.5">
              {[
                { label: 'AQA Computer Science', path: '/gcse/topics' },
                { label: 'OCR Computer Science', path: '/gcse/topics' },
                { label: 'Edexcel Computer Science', path: '/gcse/topics' },
                { label: 'WJEC Computer Science', path: '/gcse/topics' },
                { label: 'Study Guides', path: '/guides' },
                { label: 'Contact Us', path: '/contact' },
              ].map((item) => (
                <li key={item.label}>
                  <Link to={item.path} className="text-slate-400 hover:text-emerald-400 text-sm transition-colors cursor-pointer">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 - Contact & Social */}
          <div>
            <p className="text-sm font-semibold text-white mb-4">Get in Touch</p>
            <ul className="flex flex-col gap-3 mb-6">
              <li className="flex items-center gap-2 text-slate-400 text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-emerald-500"></i>
                </div>
                hi@gcsecomputing.co.uk
              </li>
              <li className="flex items-center gap-2 text-slate-400 text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-emerald-500"></i>
                </div>
                United Kingdom
              </li>
              <li className="flex items-center gap-2 text-slate-400 text-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-emerald-500"></i>
                </div>
                +44 7424 347461
              </li>
            </ul>
            <p className="text-sm font-semibold text-white mb-3">Follow Us</p>
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-youtube-line', label: 'YouTube' },
                { icon: 'ri-twitter-x-line', label: 'Twitter' },
                { icon: 'ri-instagram-line', label: 'Instagram' },
                { icon: 'ri-tiktok-line', label: 'TikTok' },
              ].map((s) => (
                <a
                  key={s.label}
                  href="#"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-emerald-700 rounded-lg transition-colors cursor-pointer border border-white/10 hover:border-emerald-700"
                >
                  <i className={`${s.icon} text-slate-300 text-base`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-slate-500 text-xs">
            &copy; 2026 GCSE Computing. Made with care for UK students.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-slate-500 hover:text-slate-300 text-xs transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" className="text-slate-500 hover:text-slate-300 text-xs transition-colors cursor-pointer">Terms of Service</a>
            <a href="#" className="text-slate-500 hover:text-slate-300 text-xs transition-colors cursor-pointer">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
